import React, { useState, useEffect, useContext, useRef } from "react";
import Select from "react-select";
import "../../assets/css/nlis.css";
import { Button, Form } from "react-bootstrap";
import classNames from "classnames";
import CustomAlert from "../general/customAlert";
import CustomTable from "../general/CustomTable";
import { deviceStatus, getNlisHoldings } from "../../api/nlis";
import AuthContext from "../../helpers/AuthContext";
import CircularProgress from "@mui/material/CircularProgress";

const columns = [
  { name: "Transaction ID", value: "transaction_id" },
  { name: "PIC", value: "pic" },
  { name: "RFID", value: "rfid" },
  { name: "NLIS ID", value: "nlis" },
  { name: "EU Status", value: "eu_status" },
  { name: "LT", value: "lt" },
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? "#1a3a5c" : "#ced4da",
    boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(26,58,92,0.25)" : "none",
    "&:hover": { borderColor: "#1a3a5c" },
    minHeight: "38px",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#1a3a5c"
      : state.isFocused
      ? "#e8eef4"
      : "white",
    color: state.isSelected ? "white" : "#212529",
    cursor: "pointer",
  }),
  placeholder: (base) => ({ ...base, color: "#6c757d" }),
};

const DeviceStatusPage = () => {
  const { user } = useContext(AuthContext);
  const abortControllerRef = useRef(null);

  const [selectedOption, setSelectedOption] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [isRFIDEnabled, setIsRFIDEnabled] = useState(true);
  const [holdings, setHoldings] = useState([]);
  const [fetchedData, setFetchedData] = useState([]);
  const [isSendingRequest, setSendingRequest] = useState(false);
  const [isMsg, setIsMsg] = useState(false);
  const [msg, setMsg] = useState(null);
  const [isError, setError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [visibleColumns, setVisibleColumns] = useState({});

  const rowsPerPage = 20;
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const handlePageChange = (page) => setCurrentPage(page);

  useEffect(() => {
    if (user?.role_id !== 1) return;

    const controller = new AbortController();

    getNlisHoldings({ signal: controller.signal })
      .then((res) => setHoldings(res.data?.holdings || res.data || []))
      .catch((err) => {
        if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
          console.error("[NLIS Holdings] fetch error:", err);
        }
      });

    return () => controller.abort();
  }, []);

  const options = holdings.map((h) => ({
    value: isRFIDEnabled ? h.rfid : h.nlis_id,
    label: isRFIDEnabled ? h.rfid : h.nlis_id,
  }));

  const handleRadioChange = (isRfid) => {
    setIsRFIDEnabled(isRfid);
    setSelectedOption(null);
    setInputValue("");
  };

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setSendingRequest(false);
    setError(true);
    setIsMsg(true);
    setMsg("Request cancelled.");
  };

  const sendSOAPRequest = async () => {
    const code = (selectedOption?.value || inputValue).trim();

    if (!code) {
      setError(true);
      setIsMsg(true);
      setMsg(isRFIDEnabled ? "Please enter or select an RFID code." : "Please enter or select an NLIS code.");
      return;
    }

    abortControllerRef.current = new AbortController();

    setSendingRequest(true);
    setError(false);
    setIsMsg(false);
    setFetchedData([]);

    try {
      const res = await deviceStatus({ code }, abortControllerRef.current.signal);

      if (res.data && res.data.length > 0) {
        setFetchedData(res.data);
        setTotalPages(Math.ceil(res.data.length / rowsPerPage));
        setVisibleColumns(
          columns.reduce((acc, item) => {
            acc[item.name] = true;
            return acc;
          }, {})
        );
      } else {
        setError(true);
        setIsMsg(true);
        setMsg("No results found for this device code.");
      }
    } catch (err) {
      if (err.name === "CanceledError" || err.code === "ERR_CANCELED") {
        return;
      }
      console.error("[NLIS] Device Status error:", err);
      setError(true);
      setIsMsg(true);
      setMsg(err?.response?.data?.message || err?.response?.data?.error || "Error fetching device status.");
    } finally {
      setSendingRequest(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !isSendingRequest) {
      const code = (selectedOption?.value || inputValue).trim();
      if (code) sendSOAPRequest();
    }
  };

  return (
    <div className="nlis-page device-status-page">
      <h4 className="mb-3 text-center text-acumen-color">NLIS Device Status</h4>

      <div className="container-fluid">
        <div className="d-flex flex-column flex-md-row">

          <div className="col-12 col-md-8 mb-1 mb-md-0 p-0">
            <div className="d-flex gap-4 mb-2">
              <div className="d-flex align-items-center">
                <input
                  type="radio"
                  name="device_type"
                  checked={isRFIDEnabled}
                  onChange={() => handleRadioChange(true)}
                  disabled={isSendingRequest}
                  className="me-2"
                />
                <Form.Label className="m-0">RFID Code</Form.Label>
              </div>
              <div className="d-flex align-items-center">
                <input
                  type="radio"
                  name="device_type"
                  checked={!isRFIDEnabled}
                  onChange={() => handleRadioChange(false)}
                  disabled={isSendingRequest}
                  className="me-2"
                />
                <Form.Label className="m-0">NLIS Code</Form.Label>
              </div>
            </div>

            <Select
              options={options}
              value={selectedOption}
              onChange={(opt) => {
                setSelectedOption(opt);
                setInputValue("");
              }}
              inputValue={inputValue}
              onInputChange={(val, { action }) => {
                if (action === "input-change") {
                  setInputValue(val);
                  setSelectedOption(null);
                }
              }}
              onKeyDown={handleKeyDown}
              isDisabled={isSendingRequest}
              isSearchable
              placeholder={isRFIDEnabled ? "Type or select RFID..." : "Type or select NLIS ID..."}
              styles={selectStyles}
              noOptionsMessage={() => holdings.length === 0 ? "Type a code to search" : "No matches found"}
            />
          </div>

          <div className="col-12 col-md-4 d-flex justify-content-md-end mt-3 mt-md-0 align-items-end gap-2">
            <Button
              className={classNames("btn btn-acumen-color", { disabled: isSendingRequest })}
              onClick={sendSOAPRequest}>
              {isSendingRequest ? "Checking..." : "Check Status"}
            </Button>
            {isSendingRequest && (
              <Button variant="outline-secondary" onClick={handleCancel}>
                Cancel
              </Button>
            )}
          </div>
        </div>

        {isSendingRequest && (
          <div className="d-flex flex-column align-items-center mt-4 gap-2">
            <CircularProgress style={{ color: "#6d4c41" }} />
            <span className="text-muted">
              Contacting NLIS — this may take up to 60 seconds...
            </span>
          </div>
        )}

        {!isSendingRequest && (
          <div className="mt-4">
            {fetchedData.length > 0 ? (
              <>
                <CustomTable
                  items={fetchedData.slice(startIndex, endIndex)}
                  columns={columns}
                  visibleColumns={visibleColumns}
                  setVisibleColumns={setVisibleColumns}
                  cantHideColumns={["pic"]}
                  maxHeight="calc(100vh - 320px)"
                  horizontalMargin="mx-1"
                />
                {totalPages > 1 && (
                  <div className="pagination mt-2">
                    <button
                      className="btn btn-acumen-color text-white me-2"
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}>
                      Previous
                    </button>
                    {Array.from({ length: totalPages }, (_, index) => (
                      <button
                        key={index + 1}
                        className={`btn ${currentPage === index + 1 ? "btn-secondary" : "btn-light"} me-1`}
                        onClick={() => handlePageChange(index + 1)}>
                        {index + 1}
                      </button>
                    ))}
                    <button
                      className="btn btn-acumen-color text-white ms-2"
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}>
                      Next
                    </button>
                  </div>
                )}
              </>
            ) : (
              !isError && (
                <div className="alert alert-warning mt-2">
                  Enter or select an RFID or NLIS code to get the status.
                </div>
              )
            )}
          </div>
        )}
      </div>

      {isMsg && (
        <CustomAlert
          isError={isError}
          message={msg}
          showMessage={isMsg}
          setShowMessage={setIsMsg}
        />
      )}
    </div>
  );
};

export default DeviceStatusPage;